<template>
    <div
        :class="{ 'vip-club-wrapper': !embedded, 'vip-embedded': embedded }"
        :id="embedded ? null : 'club-bookers-form'"
    >
        <div class="vip-club-container">
            <div class="vip-header">
                <img
                    src="/images/logo-bookers-club.svg"
                    alt="Bookers Club Logo"
                    class="vip-logo"
                />

                <h2 class="vip-title">{{ title || "Join the Club" }}</h2>
                <p class="vip-subtitle">
                    {{ trans.form.messages.club_subtitle }}
                </p>
            </div>

            <form action="#" class="vip-form" @submit.prevent="handleSubmit">
                <div class="form-group">
                    <div class="input-wrapper">
                        <span class="input-icon">&#128100;</span>
                        <input
                            v-model="fields.full_name"
                            name="full_name"
                            type="text"
                            :placeholder="trans.form.full_name"
                            class="vip-input"
                        />
                    </div>
                </div>

                <div class="form-group">
                    <div class="input-wrapper">
                        <span class="input-icon">&#128231;</span>
                        <input
                            v-model="fields.email"
                            name="email"
                            type="email"
                            :placeholder="trans.form.email"
                            class="vip-input"
                        />
                    </div>
                </div>

                <div class="form-group">
                    <div class="phone-wrapper">
                        <input
                            ref="intlPhone"
                            id="intl_phone"
                            class="intl_phone"
                            type="tel"
                            :placeholder="trans.form.phone || 'Phone'"
                        />
                    </div>
                </div>

                <div class="form-group">
                    <div class="input-wrapper">
                        <span class="input-icon">&#127758;</span>
                        <select
                            v-model="fields.country"
                            name="country"
                            class="vip-select"
                            :class="{ 'has-value': fields.country }"
                        >
                            <option disabled selected value="">{{
                                trans.form.country
                            }}</option>
                            <option
                                v-for="(value, key) in trans.form.listCountries"
                                :key="key"
                                :value="value"
                                >{{ value }}</option
                            >
                        </select>
                    </div>
                </div>

                <div class="form-group">
                    <div class="input-wrapper">
                        <span class="input-icon">&#127775;</span>
                        <select
                            v-model="fields.interest"
                            name="interest"
                            class="vip-select"
                            :class="{ 'has-value': fields.interest }"
                        >
                            <option disabled selected value="">{{
                                trans.form.interest
                            }}</option>
                            <option
                                v-for="(label, key) in trans.form.listInterests"
                                :key="key"
                                :value="key"
                                >{{ label }}</option
                            >
                        </select>
                    </div>
                </div>

                <input v-model="fields.url" name="url" type="hidden" />

                <div class="recaptcha-wrapper">
                    <vue-recaptcha
                        ref="recaptcha"
                        :loadRecaptchaScript="true"
                        :sitekey="recaptchaSiteKey"
                        @verify="onRecaptchaVerify"
                        @expired="onRecaptchaExpired"
                    ></vue-recaptcha>
                </div>

                <button class="vip-button" type="submit" :disabled="loading">
                    <span v-if="!loading">{{ trans.buttons.sign_me_up }}</span>
                    <span v-else class="button-loading">
                        <span class="spinner"></span>
                        {{ trans.buttons.loading }}
                    </span>
                </button>
            </form>
        </div>
    </div>
</template>

<script>
    import axios from 'axios'
    import Swal from 'sweetalert2'
    import VueRecaptcha from 'vue-recaptcha'
    import Alert from './AlertForm.vue'
    import intlTelInput from 'intl-tel-input'
    // CSS is loaded separately via CDN or in main.sass

export default {
    props: {
        title: { type: String, default: "" },
        trans: { type: Object, required: true },
        embedded: { type: Boolean, default: false },
        swalContainerClass: { type: String, default: "" }
    },

    components: { Alert, VueRecaptcha },

    data() {
            return {
                fields: {
                    full_name: '',
                    email: '',
                    country_code: '',
                    area_code: '',
                    phone_number: '',
                    country: '',
                    interest: '',
                    url: window.location.hostname + window.location.pathname
                },
                loading: false,
                recaptchaSiteKey: window.recaptchaSiteKey || '',
                recaptchaToken: null,
                iti: null,
                phoneError: ''
            }
        },

    mounted() {
        this.initIntlTelInput();
    },

    beforeDestroy() {
        if (this.iti) {
            this.iti.destroy();
        }
    },

    methods: {
        handleSubmit() {
            const {
                full_name,
                email,
                country_code,
                phone_number,
                country,
                interest
            } = this.fields;

            if (
                full_name === "" ||
                email === "" ||
                phone_number === '' ||
                country === "" ||
                interest === ""
            ) {
                this.showAlert(this.trans.form.messages.required, "error");
                return false;
            }

            if (this.phoneError) {
                this.showAlert(this.phoneError, 'error');
                return false;
            }

            if (!this.recaptchaToken) {
                this.showAlert(this.trans.recaptcha, "error");
                return;
            }

            this.loading = true;

            const swalOptions = this.swalContainerClass
                ? { customClass: { container: this.swalContainerClass } }
                : {};

            Swal.fire({
                title: "Aguarde...",
                allowOutsideClick: false,
                allowEscapeKey: false,
                ...swalOptions,
                didOpen: () => {
                    Swal.showLoading();
                }
            });

            const payload = {
                full_name: this.fields.full_name,
                email: this.fields.email,
                country_code: this.fields.country_code,
                area_code: this.fields.area_code,
                phone_number: this.fields.phone_number,
                country: this.fields.country,
                interest: this.fields.interest,
                "g-recaptcha-response": this.recaptchaToken
            };

            axios
                .post("/api/club-bookers-v2", payload)
                .then(response => {
                    this.resetFormData();
                    this.resetRecaptcha();
                    return this.showAlert(response.data.message, "success");
                })
                .then(() => {
                    this.$emit("submitted");
                })
                .catch(error => {
                    const message =
                        (error.response &&
                            error.response.data &&
                            error.response.data.message) ||
                        this.trans.form.messages.error;
                    this.showAlert(message, "error");
                    this.resetRecaptcha();
                })
                .finally(() => {
                    this.loading = false;
                });
        },

        onRecaptchaVerify(token) {
            this.recaptchaToken = token;
        },

        onRecaptchaExpired() {
            this.recaptchaToken = null;
        },

        resetRecaptcha() {
            this.recaptchaToken = null;
            if (this.$refs.recaptcha) {
                this.$refs.recaptcha.reset();
            }
        },

        resetFormData() {
            this.fields.full_name = "";
            this.fields.email = "";
            this.clearPhoneFields();
            this.fields.country = "";
            this.fields.interest = "";
            this.phoneError = '';

            if (this.$refs.intlPhone) {
                this.$refs.intlPhone.value = '';
            }

            const itiContainer = this.$refs.intlPhone ? this.$refs.intlPhone.closest('.phone-wrapper') : null;
            if (itiContainer) {
                itiContainer.classList.remove('phone-error');
                itiContainer.removeAttribute('data-error');
            }

            this.resetRecaptcha();
        },

        showAlert(message = "", type = "info") {
            const swalOptions = this.swalContainerClass
                ? { customClass: { container: this.swalContainerClass } }
                : {};
            return Swal.fire({
                text: message,
                icon: type,
                confirmButtonColor: (window.domainTheme && window.domainTheme.primary) || "#641467",
                ...swalOptions
            });
        },

        initIntlTelInput() {
            const input = this.$refs.intlPhone;
            if (!input) return;

            this.iti = intlTelInput(input, {
                preferredCountries: ['br', 'us', 'es'],
                initialCountry: 'us',
                separateDialCode: true,
                autoPlaceholder: 'aggressive',
                placeholderNumberType: 'MOBILE',
                utilsScript: 'https://cdn.jsdelivr.net/npm/intl-tel-input@17.0.21/build/js/utils.js',
            });

            const itiContainer = input.closest('.phone-wrapper');

            input.addEventListener('countrychange', () => {
                input.value = '';
                this.clearPhoneFields();
                this.phoneError = '';
                if (itiContainer) {
                    itiContainer.classList.remove('phone-error');
                    itiContainer.removeAttribute('data-error');
                }

                // Auto-set country field if not already selected
                if (!this.fields.country && this.iti) {
                    const countryData = this.iti.getSelectedCountryData();
                    if (countryData.name) {
                        // Try to find matching country in the list
                        const countryName = this.findCountryInList(countryData.name);
                        if (countryName) {
                            this.fields.country = countryName;
                        }
                    }
                }
            });

            input.addEventListener('input', () => {
                this.applyPhoneMask(input);
                this.validateAndSetPhone(itiContainer);
            });

            input.addEventListener('keypress', (e) => {
                // Only allow digits
                if (!/\d/.test(e.key) && e.key !== 'Backspace' && e.key !== 'Delete' && e.key !== 'Tab') {
                    e.preventDefault();
                }
            });
        },

        applyPhoneMask(input) {
            const placeholder = input.placeholder || '';
            if (!placeholder) return;

            // Get only digits from input
            const digits = input.value.replace(/\D/g, '');
            if (!digits) return;

            // Get max digits from placeholder
            const maxDigits = placeholder.replace(/\D/g, '').length;
            const limitedDigits = digits.substring(0, maxDigits);

            // Apply mask based on placeholder pattern
            let formatted = '';
            let digitIndex = 0;

            for (let i = 0; i < placeholder.length && digitIndex < limitedDigits.length; i++) {
                const placeholderChar = placeholder[i];

                if (/\d/.test(placeholderChar)) {
                    // It's a digit position - add our digit
                    formatted += limitedDigits[digitIndex];
                    digitIndex++;
                } else {
                    // It's a formatting character (space, dash, parenthesis, etc.)
                    formatted += placeholderChar;

                    // If next input char should go here, skip the formatting char
                    if (digitIndex < limitedDigits.length && i + 1 < placeholder.length && /\d/.test(placeholder[i + 1])) {
                        // Continue to next iteration
                    }
                }
            }

            // Update input value
            if (formatted !== input.value) {
                const cursorPos = input.selectionStart;
                const oldLen = input.value.length;

                input.value = formatted;

                // Adjust cursor to end or keep relative position
                const newLen = input.value.length;
                const newPos = Math.min(cursorPos + (newLen - oldLen), newLen);
                input.setSelectionRange(newPos, newPos);
            }
        },

        clearPhoneFields() {
            this.fields.country_code = '';
            this.fields.area_code = '';
            this.fields.phone_number = '';
        },

        findCountryInList(countryName) {
            const listCountries = this.trans.form.listCountries || {};

            // Direct match
            for (const key in listCountries) {
                const value = listCountries[key];
                if (value.toLowerCase() === countryName.toLowerCase()) {
                    return value;
                }
            }

            // Partial match (country name contains or is contained)
            for (const key in listCountries) {
                const value = listCountries[key];
                if (value.toLowerCase().includes(countryName.toLowerCase()) ||
                    countryName.toLowerCase().includes(value.toLowerCase())) {
                    return value;
                }
            }

            return null;
        },

        parsePhoneNumber(nationalNumber) {
            // Extract area code from parentheses in formatted number: "(11) 99999-9999" → "11"
            const areaCodeMatch = nationalNumber.match(/\(([^)]+)\)/);
            const area_code = areaCodeMatch ? areaCodeMatch[1] : '';

            // Remove all non-digits to get full number
            const fullDigits = nationalNumber.replace(/\D/g, '');

            // Remove area code from full number to get just the phone number
            const phone_number = area_code ? fullDigits.replace(area_code, '') : fullDigits;

            return { area_code, phone_number };
        },

        validateAndSetPhone(itiContainer) {
            if (!this.iti) return;

            const input = this.$refs.intlPhone;
            const inputValue = input ? input.value.trim() : '';
            const phoneErrors = this.trans.form.phoneErrors || {};

            // If empty, clear and return
            if (!inputValue) {
                this.clearPhoneFields();
                this.phoneError = '';
                if (itiContainer) {
                    itiContainer.classList.remove('phone-error');
                    itiContainer.removeAttribute('data-error');
                }
                return;
            }

            const countryData = this.iti.getSelectedCountryData();

            // Use intl-tel-input validation if utils are loaded
            if (typeof window.intlTelInputUtils !== 'undefined') {
                if (!this.iti.isValidNumber()) {
                    const errorCode = this.iti.getValidationError();
                    const errorMessages = {
                        0: phoneErrors.invalid || 'Invalid number',
                        1: phoneErrors.invalidCountry || 'Invalid country code',
                        2: phoneErrors.tooShort || 'Phone number is too short',
                        3: phoneErrors.tooLong || 'Phone number is too long',
                        4: phoneErrors.invalid || 'Invalid number',
                    };
                    this.phoneError = errorMessages[errorCode] || errorMessages[0];
                    this.clearPhoneFields();

                    if (itiContainer) {
                        itiContainer.classList.add('phone-error');
                        itiContainer.setAttribute('data-error', this.phoneError);
                    }
                    return;
                }

                // Valid - parse the number
                const nationalNumber = this.iti.getNumber(intlTelInputUtils.numberFormat.NATIONAL);
                const parsed = this.parsePhoneNumber(nationalNumber);

                this.phoneError = '';
                this.fields.country_code = countryData.dialCode;
                this.fields.area_code = parsed.area_code;
                this.fields.phone_number = parsed.phone_number;
            } else {
                // Fallback: basic validation
                const digits = inputValue.replace(/\D/g, '');
                if (digits.length < 6) {
                    this.phoneError = phoneErrors.tooShort || 'Phone number is too short';
                    this.clearPhoneFields();

                    if (itiContainer) {
                        itiContainer.classList.add('phone-error');
                        itiContainer.setAttribute('data-error', this.phoneError);
                    }
                    return;
                }

                // Parse the number (fallback without formatting)
                const parsed = this.parsePhoneNumber(inputValue);

                this.phoneError = '';
                this.fields.country_code = countryData.dialCode;
                this.fields.area_code = parsed.area_code;
                this.fields.phone_number = parsed.phone_number;
            }

            if (itiContainer) {
                itiContainer.classList.remove('phone-error');
                itiContainer.removeAttribute('data-error');
            }
        }

    }
};
</script>

<style lang="scss" scoped>
.vip-club-wrapper {
    padding: 40px 20px;
    display: flex;
    justify-content: center;
    align-items: center;
}

.vip-club-container {
    background: linear-gradient(135deg, #18015d 0%, #210c44 50%, #18015d 100%);
    border-radius: 24px;
    padding: 40px;
    max-width: 600px;
    width: 100%;
    box-shadow: 0 20px 60px rgba(18, 1, 93, 0.4), 0 0 40px rgba(25, 1, 115, 0.1);
    position: relative;
    overflow: hidden;

    &::before {
        content: "";
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        height: 4px;
        background: linear-gradient(90deg, #ffb900, #ffd700, #ffb900);
    }

    &::after {
        content: "";
        position: absolute;
        top: -50%;
        right: -50%;
        width: 100%;
        height: 100%;
        background: radial-gradient(
            circle,
            rgba(255, 185, 0, 0.05) 0%,
            transparent 70%
        );
        pointer-events: none;
    }
}

.vip-header {
    text-align: center;
    margin-bottom: 30px;
    position: relative;
    z-index: 1;
}

.vip-logo {
    width: 100%;
    max-width: 220px;
    object-fit: contain;
    margin-bottom: 20px;
}

.vip-text {
    font-size: 14px;
    font-weight: 800;
    color: var(--color-camarote-primary, #641467);
    letter-spacing: 2px;
}

.vip-title {
    font-size: 28px;
    font-weight: 700;
    line-height: 1.2;
    color: #ffffff;
    margin: 0 0 10px 0;
    text-shadow: 0 2px 10px rgba(0, 0, 0, 0.3);
}

.vip-subtitle {
    font-size: 16px;
    color: rgba(255, 255, 255, 0.7);
    margin: 0;
}

.vip-form {
    position: relative;
    z-index: 1;
}

.form-group {
    margin-bottom: 16px;
}

.input-wrapper {
    position: relative;
    width: 100%;
}

.input-icon {
    position: absolute;
    left: 16px;
    top: 50%;
    transform: translateY(-50%);
    font-size: 18px;
    line-height: 1;
    color: rgba(255, 255, 255, 0.5);
    pointer-events: none;
    z-index: 1;
}

.vip-input,
.vip-select {
    width: 100%;
    height: 54px;
    padding: 0 16px 0 50px;
    font-size: 16px;
    line-height: 54px;
    color: #ffffff;
    background: rgba(255, 255, 255, 0.08);
    border: 1px solid rgba(255, 255, 255, 0.15);
    border-radius: 12px;
    outline: none;
    transition: all 0.3s ease;

    &::placeholder {
        color: rgba(255, 255, 255, 0.4);
    }

    &:focus {
        border-color: #ffb900;
        background: rgba(255, 185, 0, 0.08);
        box-shadow: 0 0 0 3px rgba(255, 185, 0, 0.15);
    }

    option {
        background: #18015d;
        color: #ffffff;
    }
}

.vip-select {
    cursor: pointer;
    appearance: none;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' fill='rgba(255,255,255,0.5)' viewBox='0 0 16 16'%3E%3Cpath d='M8 11L3 6h10l-5 5z'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 16px center;
    padding-right: 40px;
    color: rgba(255, 255, 255, 0.4);

    &.has-value {
        color: #ffffff;
    }
}

// intl-tel-input styles (root level)
.phone-wrapper {
    position: relative;
    width: 100%;

    ::v-deep .iti {
        width: 100%;
        display: block;
    }

    ::v-deep input,
    ::v-deep input.intl_phone,
    ::v-deep input[type="tel"] {
        width: 100% !important;
        height: 54px !important;
        padding: 0 16px 0 90px !important;
        font-size: 16px !important;
        line-height: 54px !important;
        color: #ffffff !important;
        background: rgba(255, 255, 255, 0.08) !important;
        border: 1px solid rgba(255, 255, 255, 0.15) !important;
        border-radius: 12px !important;
        outline: none !important;
        transition: all 0.3s ease !important;
        box-sizing: border-box !important;
    }

    ::v-deep input::placeholder {
        color: rgba(255, 255, 255, 0.4) !important;
    }

    ::v-deep input:focus {
        border-color: #ffb900 !important;
        background: rgba(255, 185, 0, 0.08) !important;
        box-shadow: 0 0 0 3px rgba(255, 185, 0, 0.15) !important;
    }

    ::v-deep .iti__flag-container,
    ::v-deep .iti__selected-flag {
        background: transparent !important;
        border-radius: 12px 0 0 12px;
        height: 54px;
    }

    ::v-deep .iti__selected-flag {
        padding: 0 8px 0 16px;
    }

    ::v-deep .iti__arrow {
        border-top-color: rgba(255, 255, 255, 0.5);
    }

    ::v-deep .iti__arrow--up {
        border-bottom-color: rgba(255, 255, 255, 0.5);
    }

    ::v-deep .iti__country-list {
        background: #18015d;
        border: 1px solid rgba(255, 255, 255, 0.15);
        border-radius: 12px;
        box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
        max-height: 200px;
        z-index: 100;
    }

    ::v-deep .iti__country {
        padding: 10px 16px;
        color: #ffffff;
    }

    ::v-deep .iti__country:hover,
    ::v-deep .iti__country.iti__highlight {
        background: rgba(255, 185, 0, 0.15);
    }

    ::v-deep .iti__dial-code {
        color: rgba(255, 255, 255, 0.7);
    }

    ::v-deep .iti__selected-dial-code {
        color: rgba(255, 255, 255, 0.8);
        margin-left: 6px;
    }

    ::v-deep .iti__divider {
        border-bottom-color: rgba(255, 255, 255, 0.15);
    }

    &.phone-error {
        margin-bottom: 24px;

        &::after {
            content: attr(data-error);
            color: #ff6b6b;
            font-size: 12px;
            position: absolute;
            left: 8px;
            bottom: -20px;
        }

        ::v-deep input,
        ::v-deep input.intl_phone,
        ::v-deep input[type="tel"] {
            border-color: #ff6b6b !important;
        }
    }
}

.recaptcha-wrapper {
    display: flex;
    justify-content: center;
    margin: 24px 0;
    transform: scale(0.95);
    transform-origin: center;
}

.vip-button {
    display: flex;
    align-items: center;
    justify-content: center;
    width: auto;
    min-width: 200px;
    height: 54px;
    margin: 0 auto;
    padding: 0 32px;
    font-size: 16px;
    font-weight: 700;
    color: #18015d;
    background: linear-gradient(135deg, #5bebfe 0%, #5bebfe 50%, #5bebfe 100%);
    border: none;
    border-radius: 12px;
    cursor: pointer;
    transition: all 0.3s ease;
    box-shadow: 0 4px 20px rgba(91, 235, 254, 0.4);
    text-transform: uppercase;
    letter-spacing: 1px;

    &:hover:not(:disabled) {
        transform: translateY(-2px);
        box-shadow: 0 6px 30px rgba(91, 235, 254, 0.5);
    }

    &:active:not(:disabled) {
        transform: translateY(0);
    }

    &:disabled {
        opacity: 0.7;
        cursor: not-allowed;
    }
}

.button-loading {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
}

.spinner {
    width: 18px;
    height: 18px;
    border: 3px solid rgba(100, 20, 103, 0.3);
    border-top-color: var(--color-camarote-primary, #641467);
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
    flex-shrink: 0;
}

@keyframes spin {
    to {
        transform: rotate(360deg);
    }
}

.vip-disclaimer {
    text-align: center;
    font-size: 12px;
    color: rgba(255, 255, 255, 0.4);
    margin-top: 20px;
    margin-bottom: 0;
}

.vip-embedded {
    .vip-club-container {
        max-width: none;
        box-shadow: none;
        padding: 40px;
    }

    .vip-header {
        margin-bottom: 24px;
    }

    .vip-logo {
        max-width: 180px;
        margin-bottom: 16px;
    }

    .vip-title {
        font-size: 22px;
        margin-bottom: 8px;
    }

    .vip-subtitle {
        font-size: 14px;
    }

    .form-group {
        margin-bottom: 12px;
    }

    .input-icon {
        left: 14px;
        font-size: 16px;
    }

    .vip-input,
    .vip-select {
        height: 48px;
        padding: 0 14px 0 44px;
        font-size: 14px;
        line-height: 48px;
        border-radius: 10px;
    }

    .vip-select {
        background-position: right 14px center;
        padding-right: 36px;
    }

    // intl-tel-input styles
    .phone-wrapper {
        position: relative;
        width: 100%;

        ::v-deep .iti {
            width: 100%;
            display: block;
        }

        ::v-deep input,
        ::v-deep input.intl_phone,
        ::v-deep input[type="tel"] {
            width: 100% !important;
            height: 54px !important;
            padding: 0 16px 0 90px !important;
            font-size: 16px !important;
            line-height: 54px !important;
            color: #ffffff !important;
            background: rgba(255, 255, 255, 0.08) !important;
            border: 1px solid rgba(255, 255, 255, 0.15) !important;
            border-radius: 12px !important;
            outline: none !important;
            transition: all 0.3s ease !important;
            box-sizing: border-box !important;
        }

        ::v-deep input::placeholder {
            color: rgba(255, 255, 255, 0.4) !important;
        }

        ::v-deep input:focus {
            border-color: #ffb900 !important;
            background: rgba(255, 185, 0, 0.08) !important;
            box-shadow: 0 0 0 3px rgba(255, 185, 0, 0.15) !important;
        }

        ::v-deep .iti__flag-container,
        ::v-deep .iti__selected-flag {
            background: transparent !important;
            border-radius: 12px 0 0 12px;
            height: 54px;
        }

        ::v-deep .iti__selected-flag {
            padding: 0 8px 0 16px;
        }

        ::v-deep .iti__arrow {
            border-top-color: rgba(255, 255, 255, 0.5);
        }

        ::v-deep .iti__arrow--up {
            border-bottom-color: rgba(255, 255, 255, 0.5);
        }

        ::v-deep .iti__country-list {
            background: #530f5c;
            border: 1px solid rgba(255, 255, 255, 0.15);
            border-radius: 12px;
            box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
            max-height: 200px;
            z-index: 100;
        }

        ::v-deep .iti__country {
            padding: 10px 16px;
            color: #ffffff;
        }

        ::v-deep .iti__country:hover,
        ::v-deep .iti__country.iti__highlight {
            background: rgba(255, 185, 0, 0.15);
        }

        ::v-deep .iti__dial-code {
            color: rgba(255, 255, 255, 0.7);
        }

        ::v-deep .iti__selected-dial-code {
            color: rgba(255, 255, 255, 0.8);
            margin-left: 6px;
        }

        ::v-deep .iti__divider {
            border-bottom-color: rgba(255, 255, 255, 0.15);
        }

        &.phone-error {
            margin-bottom: 24px;

            &::after {
                content: attr(data-error);
                color: #ff6b6b;
                font-size: 12px;
                position: absolute;
                left: 8px;
                bottom: -20px;
            }

            ::v-deep input,
            ::v-deep input.intl_phone,
            ::v-deep input[type="tel"] {
                border-color: #ff6b6b !important;
            }
        }
    }

    .recaptcha-wrapper {
        margin: 16px 0;
        transform: scale(0.9);
    }

    .vip-button {
        min-width: 180px;
        height: 48px;
        padding: 0 28px;
        font-size: 14px;
        border-radius: 10px;
    }
}

@media (max-width: 480px) {
    .vip-club-container {
        padding: 22px 14px;
        border-radius: 18px;
    }

    .vip-title {
        font-size: 21px;
        margin-bottom: 6px;
    }

    .vip-subtitle {
        font-size: 14px;
    }

    .vip-embedded .vip-club-container {
        padding: 20px 14px;
        border-radius: 16px 16px 0 0;
    }

    .vip-header {
        margin-bottom: 16px;
    }

    .vip-logo {
        max-width: 160px;
        margin-bottom: 16px;
    }

    .form-group {
        margin-bottom: 10px;
    }

    .input-icon {
        left: 12px;
        font-size: 14px;
    }

    .vip-input,
    .vip-select {
        height: 44px;
        padding: 0 12px 0 38px;
        font-size: 14px;
        line-height: 44px;
        border-radius: 9px;
    }

    .vip-select {
        background-position: right 12px center;
        padding-right: 34px;
    }

    .recaptcha-wrapper {
        margin: 12px 0 14px;
        transform: scale(0.86);
        transform-origin: center top;
    }

    .vip-button {
        min-width: 170px;
        height: 44px;
        padding: 0 20px;
        font-size: 13px;
        border-radius: 9px;
        letter-spacing: 0.7px;
    }

    .vip-embedded .vip-club-container {
        display: flex;
        flex-direction: column;
        min-height: auto;
        border-radius: 16px 16px 0 0;
        padding: 16px 12px 14px;
    }

    .vip-embedded .vip-header {
        margin-bottom: 10px;
    }

    .vip-embedded .vip-title {
        font-size: 18px;
        line-height: 1.15;
        margin-bottom: 0;
    }

    .vip-embedded .vip-subtitle {
        display: block;
        font-size: 12px;
        margin-top: 4px;
    }

    .vip-embedded .form-group {
        margin-bottom: 8px;
    }

    .vip-embedded .input-icon {
        left: 10px;
        font-size: 13px;
    }

    .vip-embedded .vip-input,
    .vip-embedded .vip-select {
        height: 40px;
        padding: 0 10px 0 34px;
        font-size: 13px;
        line-height: 40px;
        border-radius: 8px;
    }

    .vip-embedded .vip-select {
        background-position: right 10px center;
        padding-right: 30px;
    }

    .vip-embedded .recaptcha-wrapper {
        margin: 8px 0;
        transform: scale(0.78);
    }

    .vip-embedded .vip-button {
        min-width: 150px;
        height: 40px;
        padding: 0 14px;
        font-size: 12px;
        border-radius: 8px;
        letter-spacing: 0.6px;
    }
}

@media (max-width: 380px) {
    .vip-club-wrapper {
        padding: 20px 10px;
    }

    .vip-club-container {
        padding: 18px 12px;
    }

    .vip-embedded .vip-club-container {
        padding: 18px 12px;
    }

    .vip-title {
        font-size: 19px;
    }

    .vip-subtitle {
        font-size: 13px;
    }

    .vip-input,
    .vip-select {
        height: 44px;
        font-size: 13px;
        line-height: 44px;
    }

    .recaptcha-wrapper {
        transform: scale(0.8);
    }

    .vip-button {
        min-width: 160px;
        height: 44px;
        font-size: 12px;
    }

    .vip-embedded .vip-club-container {
        padding: 14px 10px 12px;
    }

    .vip-embedded .vip-logo {
        max-width: 110px;
        margin-bottom: 16px;
    }

    .vip-embedded .vip-title {
        font-size: 16px;
    }

    .vip-embedded .vip-input,
    .vip-embedded .vip-select {
        height: 37px;
        font-size: 12px;
        line-height: 37px;
        padding-left: 32px;
    }

    .vip-embedded .recaptcha-wrapper {
        margin: 6px 0 8px;
        transform: scale(0.72);
    }

    .vip-embedded .vip-button {
        min-width: 140px;
        height: 37px;
        font-size: 11px;
    }
}
</style>
